import React, {Component} from 'react';
import PortalModal from '../layout/portal-modal.component';
import styles from './plans.module.scss';

export type CancelReason =
    | 'TOO_EXPENSIVE'
    | 'DONT_USE_ENOUGH'
    | 'MISSING_FEATURE'
    | 'SWITCHING'
    | 'OTHER';

export interface CancelModalProps {
    open: boolean;
    productLabel: string;
    productSummary: string;
    activeUntil: string | null;
    extraBullets?: string[];
    onKeep: () => void;
    onConfirm: (payload: {reason: CancelReason | null}) => void;
}

interface State {
    reason: CancelReason | '';
    submitting: boolean;
}

/**
 * Shared confirmation modal for cancelling a paid subscription.
 *
 * Used by both /portal/billing (tile cancel) and /portal/plans
 * (Downgrade-to-Free). The Keep button is the default-focused
 * "safe" action; Escape maps to Keep. The reason dropdown is
 * optional and never blocks submission.
 *
 * The component owns its own window-level Escape listener so the
 * key reliably routes to [onKeep]. PortalModal's built-in Esc
 * handler is suppressed via [closeOnEsc]={false} to avoid the
 * callback firing twice in production.
 */
export class CancelModal extends Component<CancelModalProps, State> {
    state: State = {reason: '', submitting: false};

    componentDidMount(): void {
        if (typeof window !== 'undefined') {
            window.addEventListener('keydown', this.onKeyDown);
        }
    }

    componentWillUnmount(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener('keydown', this.onKeyDown);
        }
    }

    private onKeyDown = (e: KeyboardEvent): void => {
        if (!this.props.open) return;
        if (e.key === 'Escape') this.props.onKeep();
    };

    private formatDate(iso: string | null): string {
        if (!iso) return 'the end of your current billing period';
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC',
            });
        } catch {
            return iso;
        }
    }

    private onReasonChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        this.setState({reason: e.target.value as CancelReason | ''});
    };

    private onSubmit = (): void => {
        if (this.state.submitting) return;
        this.setState({submitting: true});
        this.props.onConfirm({reason: this.state.reason || null});
    };

    render(): React.ReactNode {
        if (!this.props.open) return null;
        const {productLabel, productSummary, activeUntil, extraBullets, onKeep} = this.props;
        const {reason, submitting} = this.state;
        return (
            <PortalModal
                onClose={onKeep}
                closeOnEsc={false}
                backdropClassName={styles.modalBackdrop}
                dialogClassName={styles.modalDialog}
                aria-label={`Cancel ${productLabel} subscription`}
                dataTestId="cancel-modal"
            >
                <div className={styles.modalBody}>
                    <h2 className={styles.modalTitle}>Cancel {productLabel} subscription?</h2>
                    <p className={styles.modalLead}>
                        You&apos;ll keep {productLabel} access until {this.formatDate(activeUntil)}.
                        After that, your account returns to the Free tier.
                    </p>
                    <ul className={styles.modalBullets}>
                        <li>{productSummary} access ends</li>
                        {extraBullets?.map((b, i) => (
                            <li key={i}>{b}</li>
                        ))}
                    </ul>
                    <label className={styles.modalReason}>
                        Reason for cancelling (optional)
                        <select
                            value={reason}
                            onChange={this.onReasonChange}
                            aria-label="Reason for cancelling"
                        >
                            <option value="">— Select if you&apos;d like —</option>
                            <option value="TOO_EXPENSIVE">Too expensive</option>
                            <option value="DONT_USE_ENOUGH">Don&apos;t use it enough</option>
                            <option value="MISSING_FEATURE">Missing a feature I need</option>
                            <option value="SWITCHING">Switching to another service</option>
                            <option value="OTHER">Other</option>
                        </select>
                    </label>
                    <div className={styles.modalActions}>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={onKeep}
                            autoFocus
                        >
                            Keep my plan
                        </button>
                        <button
                            type="button"
                            className={styles.btnDanger}
                            onClick={this.onSubmit}
                            disabled={submitting}
                        >
                            {submitting ? 'Cancelling…' : 'Cancel subscription'}
                        </button>
                    </div>
                </div>
            </PortalModal>
        );
    }
}
